import { FetchAPI, FieldAPI, IdentifiableAPI, PanelAPI, SectionAPI, SortableAPI, TimeFieldAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Value } from '@philips-internal/oswald-ui-core/lib/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';

import { Inject } from '@odinjs/odin';

import { CpoeDeckAliases, FragmentNames } from '../../../codegen';
import { ISO_DATE_INPUT } from '../../../constants/date-utils';

import BaseController from '../../BaseController';

import moment, { Moment } from 'moment';
import { ApplicationRoute } from '../../../constants/routes';
import CpoeService, { allowViewGroup, CpoeConfigurations, CpoeExpressions, CpoeGroup, NutritionType, OrderGroup, RuleGroup } from '../services/cpoe-service';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { DateFilterUtils } from '../../../../src/utils/date-filter-utils';
import { FoundationUtils } from '../../../../src/utils/foundation-utils';
import { CPOE_9138Parameters, FunctionName } from '../../../../src/utils/parameters';
import { YesNoType } from '../../../../src/utils/enum-utils';

const closeGroup: Record<CpoeGroup, boolean> = {
  [CpoeGroup.DIALYSIS]: true,
  [CpoeGroup.EXAMS_PROCEDURES]: true,
  [CpoeGroup.GASTHERAPY]: true,
  [CpoeGroup.HEMOTHERAPY]: true,
  [CpoeGroup.INFUSIONS_MEDICINES]: true,
  [CpoeGroup.NURSING_INTERVENTIONS]: true,
  [CpoeGroup.NUTRITION]: true,
  [CpoeGroup.RECOMMENDATIONS]: true,
};

interface SectionInfo {
  api: SectionAPI;
  expression: string;
  sectionKey: string;
}

enum FilterItens {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  ANY = 'any'
}

const ISO_HOURS_MINUTES_SECONDS_FORMAT = 'HH:mm:ss[Z]';
const TEEN_VALUE = 10;

@Controller({ fragment: FragmentNames.CPOE_DECK })
class CpoeDeckController extends BaseController {

  @Inject(CpoeDeckAliases.FIELD_CPOE_FILTER_DATE)
    dateCpoeFilterApi!: FieldAPI;

  @Inject(CpoeDeckAliases.FIELD_CPOE_FILTER_HOUR)
    hourCpoeFilterApi!: FieldAPI;

  @Inject(CpoeDeckAliases.SORTABLE_CPOE)
    sortableCpoeApi!: SortableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_DIALYSIS)
    identifiableDialysisApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_EXAMS)
    identifiableExamsApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_GAS_THERAPY)
    identifiableGasTherapyApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_HEMOTHERAPY)
    identifiableHemotherapyApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_INFUSION)
    identifiableInfusionApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_INTERVENTATIONS)
    identifiableInterventationsApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_NUTRITION)
    identifiableNutritionsApi!: IdentifiableAPI;

  @Inject(CpoeDeckAliases.IDENTIFIABLE_RECOMMENDATIONS)
    identifiableRecommendationsApi!: IdentifiableAPI;

  sectionDialysisApi!: SectionAPI;

  sectionExamsApi!: SectionAPI;

  sectionGasTherapyApi!: SectionAPI;

  sectionHemotherapyApi!: SectionAPI;

  sectionInfusionApi!: SectionAPI;

  sectionNursingApi!: SectionAPI;

  sectionNutritionApi!: SectionAPI;

  sectionRecommendationApi!: SectionAPI;

  fetchDialysisApi!: FetchAPI;

  fetchExamsApi!: FetchAPI;

  fetchGasTherapyApi!: FetchAPI;

  fetchHemotherapyApi!: FetchAPI;

  fetchInfusionApi!: FetchAPI;

  fetchNursingApi!: FetchAPI;

  fetchNutritionApi!: FetchAPI;

  fetchRecommendationsApi!: FetchAPI;

  @OnMount({ target: CpoeDeckAliases.SORTABLE_CPOE })
  handleMountSortableCPOE(): void {
    this.getConfigurationsCpoe()
      .then((configurations: CpoeConfigurations) => {
        this.setOrderGroups(configurations);
      })
  }

  @OnMount(CpoeDeckAliases.FETCH_DIALYSIS)
  handleMountDialysisFetch(event: MountEvent): void {
    this.fetchDialysisApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_EXAMS_AND_PROCEDURES)
  handleMountExamsFetch(event: MountEvent): void {
    this.fetchExamsApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_GAS_THERAPY)
  handleMountGasTherapyFetch(event: MountEvent): void {
    this.fetchGasTherapyApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_HEMOTHERAPY)
  handleMountHemotherapyFetch(event: MountEvent): void {
    this.fetchHemotherapyApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_INFUSION_MEDICINS)
  handleMountInfusionFetch(event: MountEvent): void {
    this.fetchInfusionApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_NURSING_INTERVENTIONS)
  handleMountNursingFetch(event: MountEvent): void {
    this.fetchNursingApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_NUTRITION)
  handleMountNutritionFetch(event: MountEvent): void {
    this.fetchNutritionApi = event.target as FetchAPI;
  }

  @OnMount(CpoeDeckAliases.FETCH_RECOMMENDATIONS)
  handleMountRecommendationsFetch(event: MountEvent): void {
    this.fetchRecommendationsApi = event.target as FetchAPI;
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_DIALYSIS })
  handleMountDialysisSection(event: MountEvent): void {
    this.sectionDialysisApi = event.target as SectionAPI;
    this.sectionDialysisApi.setIcon('static/assets/dialysis.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_GASTHERAPY })
  handleMountGasTherapySection(event: MountEvent): void {
    this.sectionGasTherapyApi = event.target as SectionAPI;
    this.sectionGasTherapyApi.setIcon('static/assets/gas.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_HEMOTHERAPY })
  handleMountHemotherapySection(event: MountEvent): void {
    this.sectionHemotherapyApi = event.target as SectionAPI;
    this.sectionHemotherapyApi.setIcon('static/assets/transfusion.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_INFUSION_MEDICINS })
  handleMountInfusionSection(event: MountEvent): void {
    this.sectionInfusionApi = event.target as SectionAPI;
    this.sectionInfusionApi.setIcon('static/assets/needle.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_NURSING_INTERVENTIONS })
  handleMountNursingSection(event: MountEvent): void {
    this.sectionNursingApi = event.target as SectionAPI;
    this.sectionNursingApi.setIcon('static/assets/nurse-prescription.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_NUTRITION })
  handleMountNutritionSection(event: MountEvent): void {
    this.sectionNutritionApi = event.target as SectionAPI;
    this.sectionNutritionApi.setIcon('static/assets/cutlery.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_EXAMS_PROCEDURES })
  handleMountExamsSection(event: MountEvent): void {
    this.sectionExamsApi = event.target as SectionAPI;
    this.sectionExamsApi.setIcon('static/assets/lungs.svg');
  }

  @OnMount({ target: CpoeDeckAliases.SECTION_RECOMMENDATIONS })
  handleMountRecommendationsSection(event: MountEvent): void {
    this.sectionRecommendationApi = event.target as SectionAPI;
    this.sectionRecommendationApi.setIcon('static/assets/notes.svg');
  }

  @OnMount(CpoeDeckAliases.TIME_CPOE_FILTER)
  handleMountTimeCpoeFilter(event: MountEvent): void {
    const timeCpoeFilterApi = event.target as TimeFieldAPI;
    timeCpoeFilterApi.setShowClearAction(false);
  }

  @OnUnmount({ target: CpoeDeckAliases.LAYOUT_CPOE })
  handleLayoutCpoeUnmount(): void {
    this.resetValuesCloseSection();
  }

  @OnClick(CpoeDeckAliases.CARDCONTAINER_NUTRITION)
  handleClickDeckNutrition(event: ClickEvent): void {
    const { id, typeDiet } = event.value;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    const route = this.getNutritionViewRouteByType(typeDiet);
    applicationContext.redirect(route, {
      encounterId,
      nutritionId: id,
      patientId,
    })
  }

  @OnClick(CpoeDeckAliases.CARDCONTAINER_INFUSION_MEDICINS)
  handleClickDeckInfusionMedicins(event: ClickEvent): void {
    const { id } = event.value;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(ApplicationRoute.CPOE_MEDICINE_DETAILS, {
      encounterId,
      medicineId: id,
      patientId,
    })
  }

  @OnBeforeFetch({
    target: [
      CpoeDeckAliases.FETCH_INFUSION_MEDICINS, CpoeDeckAliases.FETCH_DIALYSIS,
      CpoeDeckAliases.FETCH_EXAMS_AND_PROCEDURES, CpoeDeckAliases.FETCH_GAS_THERAPY, CpoeDeckAliases.FETCH_HEMOTHERAPY,
      CpoeDeckAliases.FETCH_NURSING_INTERVENTIONS, CpoeDeckAliases.FETCH_NUTRITION, CpoeDeckAliases.FETCH_RECOMMENDATIONS]
  })
  handleBeforeCpoeMedicineCpoe(evt: FetchEvent): void {
    const fetch = evt.target as FetchAPI;
    if (fetch) {
      fetch.setParameters(this.getParametersRoutes(FilterItens.ANY));
    }
  }

  @OnChange({ target: CpoeDeckAliases.FIELD_CPOE_FILTER_DATE })
  handleChangeCpoeFilterDate({ value }: ClickEvent): void {
    const date = moment(value);
    if (value.length == TEEN_VALUE && moment(date).isValid()) {
      this.dateCpoeFilterApi.setData({ date: moment(date).utc().format(ISO_DATE_FORMAT) });
      this.forceFetchAllGroups();
    }
  }

  @OnMount({ target: CpoeDeckAliases.FIELD_CPOE_FILTER_DATE })
  handleMountFieldCpoeFilterDate(): void {
    const currentDate = DateFilterUtils.getCurrentDateUtc();
    this.dateCpoeFilterApi.setData({ date: currentDate });
  }

  @OnChange({ target: CpoeDeckAliases.FIELD_CPOE_FILTER_HOUR })
  handleChangeCpoeFilterHour(event: ClickEvent): void {
    this.hourCpoeFilterApi.setData({ hour: event.value });
    this.forceFetchAllGroups();
  }

  @OnMount({ target: CpoeDeckAliases.FIELD_CPOE_FILTER_HOUR })
  handleMountFieldCpoeFilterHour(): void {
    const currentTime = DateFilterUtils.getCurrentTimeUtc();
    this.hourCpoeFilterApi.setData({ hour: currentTime });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_NUTRITION })
  handleAfterFetchNutrition(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionNutritionApi,
      expression: CpoeExpressions.Nutrition,
      sectionKey: CpoeGroup.NUTRITION
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_EXAMS_AND_PROCEDURES })
  handleAfterFetchExams(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionExamsApi,
      expression: CpoeExpressions.Exam,
      sectionKey: CpoeGroup.EXAMS_PROCEDURES,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_HEMOTHERAPY })
  handleAfterFetchHemotherapy(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionHemotherapyApi,
      expression: CpoeExpressions.Hemotherapy,
      sectionKey: CpoeGroup.HEMOTHERAPY,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_DIALYSIS })
  handleAfterFetchDialysis(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionDialysisApi,
      expression: CpoeExpressions.Dialysis,
      sectionKey: CpoeGroup.DIALYSIS,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_INFUSION_MEDICINS })
  handleAfterFetchInfusion(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionInfusionApi,
      expression: CpoeExpressions.Infusion,
      sectionKey: CpoeGroup.INFUSIONS_MEDICINES,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_RECOMMENDATIONS })
  handleAfterFetchRecommendations(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionRecommendationApi,
      expression: CpoeExpressions.Recommendation,
      sectionKey: CpoeGroup.RECOMMENDATIONS,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_NURSING_INTERVENTIONS })
  handleAfterFetchNursing(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionNursingApi,
      expression: CpoeExpressions.Interventation,
      sectionKey: CpoeGroup.NURSING_INTERVENTIONS,
    });
  }

  @OnAfterFetch({ target: CpoeDeckAliases.FETCH_GAS_THERAPY })
  handleAfterFetchGasTherapy(event: FetchEvent): void {
    this.handleAfterFetch(event, {
      api: this.sectionGasTherapyApi,
      expression: CpoeExpressions.Gastherapy,
      sectionKey: CpoeGroup.GASTHERAPY,
    });
  }

  getParametersRoutes(filterStatus: FilterItens): Record<string, Value> {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const { revalidationRuleId } = this.context.getBulkData()
    const hourFilterCpoe = this.hourCpoeFilterApi.getValue() as string;
    const hourCpoe = moment(hourFilterCpoe, ISO_HOURS_MINUTES_SECONDS_FORMAT).utcOffset(hourFilterCpoe);
    const momentCpoe = moment(this.dateCpoeFilterApi.getValue() as string) as Moment;
    const dateCpoe = momentCpoe.hour(hourCpoe.hour()).minute(hourCpoe.minutes());
    const dtReference = dateCpoe.format(ISO_DATE_INPUT);
    const prescriptionId = 0;
    const scheduleId = 0;
    const statusItem = filterStatus;
    return {
      dtReference,
      encounterId,
      patientId,
      prescriptionId,
      revalidationRuleId: revalidationRuleId || '',
      scheduleId,
      statusItem
    } as Record<string, Value>;
  }

  checkPermissionViewGroup(rule: RuleGroup, panelApi: PanelAPI): void {
    if (!allowViewGroup(rule)) {
      panelApi.setVisible(false);
    }
  }

  async getConfigurationsCpoe(): Promise<CpoeConfigurations> {
    const configurations = this.context.get('configurationsCpoe');
    return configurations ? configurations : await CpoeService.loadConfigurationsCpoe();
  }

  setOrderGroups(configurationsCpoe: CpoeConfigurations): void {
    const orderedGroups: OrderGroup[] = [
      { group: CpoeGroup.NUTRITION, order: configurationsCpoe.orderOralDiet ?? 0,
        view: allowViewGroup(configurationsCpoe.ruleOralDiet) },
      { group: CpoeGroup.INFUSIONS_MEDICINES, order: configurationsCpoe.orderMedicine ?? 1,
        view: allowViewGroup(configurationsCpoe.ruleMedicine) },
      { group: CpoeGroup.EXAMS_PROCEDURES, order: configurationsCpoe.orderExam ?? 2,
        view: allowViewGroup(configurationsCpoe.ruleExam) },
      { group: CpoeGroup.GASTHERAPY, order: configurationsCpoe.orderGasTherapy ?? 3,
        view: allowViewGroup(configurationsCpoe.ruleGasTherapy) },
      { group: CpoeGroup.RECOMMENDATIONS, order: configurationsCpoe.orderRecommendation ?? 4,
        view: allowViewGroup(configurationsCpoe.ruleRecommendation) },
      { group: CpoeGroup.HEMOTHERAPY, order: configurationsCpoe.orderHemotherapy ?? 5,
        view: allowViewGroup(configurationsCpoe.ruleHemotherapy) },
      { group: CpoeGroup.DIALYSIS, order: configurationsCpoe.orderDialysis ?? 6,
        view: allowViewGroup(configurationsCpoe.ruleDialysis) },
      { group: CpoeGroup.NURSING_INTERVENTIONS, order: configurationsCpoe.orderInterventation ?? 7,
        view: allowViewGroup(configurationsCpoe.ruleInterventation) },
    ]?.sort(this.sortGroups);
    const order = orderedGroups.filter(item => !!item.view).map(item => item.group);
    this.sortableCpoeApi.setOrder(order ?? []);
  }

  sortGroups( orderGroupA: OrderGroup, orderGroupB: OrderGroup ): number {
    return orderGroupA.order - orderGroupB.order;
  }

  forceFetchAllGroups(): void {
    this.resetValuesCloseSection();

    if (this.sectionDialysisApi) {
      this.sectionDialysisApi.isOpen() ? this.fetchDialysisApi.forceFetch() : this.sectionDialysisApi.open();
    }
    if (this.sectionInfusionApi) {
      this.sectionInfusionApi.isOpen() ? this.fetchInfusionApi.forceFetch() : this.sectionInfusionApi.open();
    }
    if (this.sectionExamsApi) {
      this.sectionExamsApi.isOpen() ? this.fetchExamsApi.forceFetch() : this.sectionExamsApi.open();
    }
    if (this.sectionGasTherapyApi) {
      this.sectionGasTherapyApi.isOpen() ? this.fetchGasTherapyApi.forceFetch() : this.sectionGasTherapyApi.open();
    }
    if (this.sectionHemotherapyApi) {
      this.sectionHemotherapyApi.isOpen() ? this.fetchHemotherapyApi.forceFetch() : this.sectionHemotherapyApi.open();
    }
    if (this.sectionNursingApi) {
      this.sectionNursingApi.isOpen() ? this.fetchNursingApi.forceFetch() : this.sectionNursingApi.open();
    }
    if (this.sectionNutritionApi) {
      this.sectionNutritionApi.isOpen() ? this.fetchNutritionApi.forceFetch() : this.sectionNutritionApi.open();
    }
    if (this.sectionRecommendationApi) {
      this.sectionRecommendationApi.isOpen() ? this.fetchRecommendationsApi.forceFetch() : this.sectionRecommendationApi.open();
    }
  }

  private getNutritionViewRouteByType(typeDiet: string): ApplicationRoute {
    switch (typeDiet) {
      case NutritionType.ENTERAL_DIETS:
        return ApplicationRoute.CPOE_NUTRITION_ENTERAL_DIET_DETAILS;
      case NutritionType.FLUID_CONTROL:
        return ApplicationRoute.CPOE_NUTRITION_WATER_BALANCE_DETAILS;
      case NutritionType.INFANT_MILKS_FORMULAS:
        return ApplicationRoute.CPOE_NUTRITION_MILK_AND_DERIVATIVES_DETAILS;
      case NutritionType.SUPPLEMENTS:
        return ApplicationRoute.CPOE_NUTRITION_SUPPLEMENTS_DETAILS;
      case NutritionType.FASTING:
        return ApplicationRoute.CPOE_NUTRITION_FASTING_DETAILS;
      case NutritionType.ORAL_DIETS:
        return ApplicationRoute.CPOE_NUTRITION_ORAL_DIET_DETAILS
      default:
        return ApplicationRoute.CPOE;
    }
  }

  private handleAfterFetch(event: FetchEvent, sectionInfo: SectionInfo): void {
    const data = event.data as [] ?? [];
    sectionInfo.api.setTitle(MessageService.translate(sectionInfo.expression, { count: data.length }));
    const keepGroupsClosed = this.getParamAccess(FunctionName.CPOE_9138, CPOE_9138Parameters.KEEP_GROUPS_CPOE_CLOSED) as string;
    if (keepGroupsClosed?.toUpperCase() === YesNoType.YES && closeGroup[sectionInfo.sectionKey as keyof typeof closeGroup]) {
      closeGroup[sectionInfo.sectionKey as keyof typeof closeGroup] = false;
      sectionInfo.api.close();
    }
  }

  getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private resetValuesCloseSection(): void {
    closeGroup.Dialysis = true;
    closeGroup.Exam = true;
    closeGroup.Gastherapy = true;
    closeGroup.Hemotherapy = true;
    closeGroup.Infusion = true;
    closeGroup.Interventation = true;
    closeGroup.Nutrition = true;
    closeGroup.Recommendation = true;
  }
}

export default CpoeDeckController;
